import clsx from 'clsx'
import { Suspense, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { logout } from '@/shared/api/session'
import { useBreakpoint } from '@/shared/hooks/useBreakpoint'
import { Avatar, Button, LanguageSwitcher, Spinner, ThemeToggle } from '@/shared/ui'
import AppFooter from '@/shared/ui/AppFooter'
import { BrandIcon, BrandMark, Logo } from '@/shared/ui/brand'
import { useSessionStore } from '@/shared/store/session'

function MenuIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function CloseIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  )
}

function SearchIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <circle cx="11" cy="11" r="7" />
      <path strokeLinecap="round" d="M21 21l-4.35-4.35" />
    </svg>
  )
}

function PlusIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" {...props}>
      <path strokeLinecap="round" d="M12 5v14M5 12h14" />
    </svg>
  )
}

function ChevronIcon({ flipped, className, ...props }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={clsx('transition-transform', flipped && 'rotate-180', className)}
      {...props}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 6l-6 6 6 6" />
    </svg>
  )
}

function useNavItems(t) {
  return [
    { to: '/empresa', end: true, icon: 'analytics', label: t('company.dashboard') },
    { to: '/empresa/vacantes', icon: 'vacancies', label: t('company.jobs') },
    { to: '/empresa/suscripcion', icon: 'analytics', label: t('company.subscription') },
    {
      to: '/empresa/mi-disponibilidad',
      icon: 'analytics',
      label: t('company.availability.navLabel'),
    },
  ]
}

function SidebarNavItem({ item, iconOnly }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      title={iconOnly ? item.label : undefined}
      className={({ isActive }) => clsx('humania-nav__item', isActive && 'is-active')}
    >
      <BrandIcon name={item.icon} className="humania-nav__icon size-5 shrink-0" />
      <span className={iconOnly ? 'sr-only' : ''}>{item.label}</span>
    </NavLink>
  )
}

/** Identidad de quien tiene la sesión abierta, junto al botón de salir. */
function UserChip({ name }) {
  return (
    <span className="hidden min-w-0 items-center gap-2 rounded-full bg-ink-50 py-1 pl-1 pr-3 md:flex">
      <Avatar name={name} className="size-7 text-[11px]" />
      <span className="max-w-40 truncate text-sm font-medium text-ink-900">{name}</span>
    </span>
  )
}

function BottomNavTab({ item }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      className={({ isActive }) => clsx('humania-bottomnav__tab', isActive && 'is-active')}
    >
      <BrandIcon name={item.icon} className="size-5 shrink-0" />
      <span>{item.label}</span>
    </NavLink>
  )
}

/**
 * Shell de 3 niveles, mismo patrón que Safyro Finanzas (app/AppShell.tsx):
 *  - desktop (≥1280): sidebar con etiquetas, siempre visible.
 *  - tablet (768–1279): rail angosto de solo íconos.
 *  - mobile (<768): BottomNavBar + FAB, y el ☰ abre el panel completo.
 */
export default function CompanyLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const bp = useBreakpoint()
  const user = useSessionStore((state) => state.user)
  const [navOpen, setNavOpen] = useState(false)
  // Solo aplica en desktop: en tablet/mobile el shell ya es angosto o vive en
  // el panel superpuesto, no hace falta otro control para lo mismo.
  const [desktopCollapsed, setDesktopCollapsed] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const navItems = useNavItems(t)

  useEffect(() => setNavOpen(false), [location.pathname, bp])

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const onSearchSubmit = (event) => {
    event.preventDefault()
    const trimmed = searchTerm.trim()
    navigate(trimmed ? `/empresa/vacantes?q=${encodeURIComponent(trimmed)}` : '/empresa/vacantes')
  }

  return (
    <div className="humania-app flex min-h-full">
      {bp === 'desktop' ? (
        <aside
          className={clsx(
            'humania-sidebar hidden shrink-0 flex-col md:flex',
            desktopCollapsed ? 'w-22 px-2' : 'w-65'
          )}
        >
          <div className="mb-7">
            <div className={clsx('flex items-center', desktopCollapsed && 'justify-center')}>
              {desktopCollapsed ? <BrandMark className="rounded-xl" /> : <Logo className="h-6" />}
            </div>
            {desktopCollapsed ? null : (
              <span className="mt-2 inline-block rounded-full bg-ink-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                {t('nav.profileCompany')}
              </span>
            )}
          </div>

          <nav className="humania-nav">
            {navItems.map((item) => (
              <SidebarNavItem key={item.to} item={item} iconOnly={desktopCollapsed} />
            ))}
          </nav>
        </aside>
      ) : null}

      {bp === 'tablet' ? (
        <nav className="humania-navrail" aria-label={t('nav.menu')}>
          <BrandMark className="mx-auto rounded-xl" />
          {navItems.map((item) => (
            <SidebarNavItem key={item.to} item={item} iconOnly />
          ))}
        </nav>
      ) : null}

      {bp !== 'desktop' && navOpen ? (
        <div className="humania-nav-overlay" onClick={() => setNavOpen(false)}>
          <nav
            className="humania-nav-panel"
            aria-label={t('nav.menu')}
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-2">
              <Logo className="h-7" />
              <button
                type="button"
                aria-label={t('nav.collapse')}
                onClick={() => setNavOpen(false)}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
              >
                <CloseIcon className="size-5" />
              </button>
            </div>
            <nav className="humania-nav">
              {navItems.map((item) => (
                <SidebarNavItem key={item.to} item={item} />
              ))}
            </nav>
            <div className="mt-auto space-y-3 border-t border-ink-100 px-2 pt-4">
              {user ? (
                <span className="flex min-w-0 items-center gap-2 rounded-full bg-ink-50 py-1 pl-1 pr-3">
                  <Avatar name={user.full_name} className="size-7 text-[11px]" />
                  <span className="min-w-0 truncate text-sm font-medium text-ink-900">
                    {user.full_name}
                  </span>
                </span>
              ) : null}
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <ThemeToggle />
                  <LanguageSwitcher />
                </div>
                <Button variant="ghost" onClick={handleLogout}>
                  {t('nav.logout')}
                </Button>
              </div>
            </div>
          </nav>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex items-stretch border-b border-(--humania-shell-border) bg-(--humania-surface)">
          {bp === 'desktop' ? (
            // Pegado al borde del sidebar (sin el padding del resto del
            // header) y del mismo alto, como una pestaña de esa misma pieza.
            <button
              type="button"
              aria-label={desktopCollapsed ? t('nav.expand') : t('nav.collapse')}
              onClick={() => setDesktopCollapsed((collapsed) => !collapsed)}
              className="grid w-12 shrink-0 place-items-center border-r border-(--humania-shell-border) text-ink-500 transition hover:bg-ink-50 hover:text-ink-900"
            >
              <ChevronIcon flipped={desktopCollapsed} className="size-4" />
            </button>
          ) : null}

          <div className="mx-auto flex min-w-0 flex-1 max-w-[1600px] items-center gap-3 px-4 py-3 sm:gap-4 sm:px-6">
            {bp !== 'desktop' ? (
              <button
                type="button"
                aria-label={t('nav.menu')}
                aria-expanded={navOpen}
                onClick={() => setNavOpen((open) => !open)}
                className="grid size-9 shrink-0 place-items-center rounded-lg border border-ink-200 text-ink-600 transition hover:bg-ink-50 hover:text-ink-900"
              >
                <MenuIcon className="size-5" />
              </button>
            ) : null}

            <form onSubmit={onSearchSubmit} role="search" className="min-w-0 flex-1">
              <label htmlFor="company-search" className="sr-only">
                {t('company.searchPlaceholder')}
              </label>
              <div className="flex items-center rounded-lg border border-ink-200 bg-(--humania-bg) pl-4 pr-1.5">
                <input
                  id="company-search"
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t('company.searchPlaceholder')}
                  className="min-w-0 flex-1 bg-transparent py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none"
                />
                <button
                  type="submit"
                  aria-label={t('jobs.search')}
                  className="grid size-8 shrink-0 place-items-center rounded-md bg-brand-600 text-white transition hover:bg-brand-700"
                >
                  <SearchIcon className="size-4" />
                </button>
              </div>
            </form>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <ThemeToggle />
              <div className="hidden sm:block">
                <LanguageSwitcher />
              </div>
              {user ? <UserChip name={user.full_name} /> : null}
              <Button variant="ghost" onClick={handleLogout} className="hidden sm:inline-flex">
                {t('nav.logout')}
              </Button>
            </div>
          </div>
        </header>

        <main className={clsx('flex-1 p-6', bp === 'mobile' && 'pb-24')}>
          <Suspense fallback={<Spinner />}>
            <Outlet />
          </Suspense>
        </main>

        {bp !== 'mobile' ? <AppFooter /> : null}
      </div>

      {bp === 'mobile' ? (
        <>
          <button
            type="button"
            className="humania-fab"
            aria-label={t('company.newJob')}
            onClick={() => navigate('/empresa/vacantes')}
          >
            <PlusIcon className="size-6" />
          </button>
          <nav className="humania-bottomnav" aria-label={t('nav.menu')}>
            {navItems.map((item) => (
              <BottomNavTab key={item.to} item={item} />
            ))}
          </nav>
        </>
      ) : null}
    </div>
  )
}
